// Curated vivid palette for Tag chips (GitHub-label style). Hues are spread so
// adjacent picks read as distinct. Text color is computed per chip from the
// background luminance, so any of these stays legible.
export const TAG_PALETTE = [
  '#D73A4A', // red
  '#E36209', // orange
  '#DBAB09', // amber
  '#2DA44E', // green
  '#0E8A6E', // teal
  '#0969DA', // blue
  '#6F42C1', // purple
  '#BF3989', // magenta
  '#8B5E34', // brown
  '#57606A', // slate
] as const;

// Pick the least-used palette color (ties broken by palette order) so colors
// spread evenly across the tag bank as new tags are created.
export function pickTagColor(usedColors: string[]): string {
  const counts = new Map<string, number>(TAG_PALETTE.map((c) => [c, 0]));
  for (const c of usedColors) {
    const key = c.toUpperCase();
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let best: string = TAG_PALETTE[0];
  let bestCount = Infinity;
  for (const c of TAG_PALETTE) {
    const n = counts.get(c) ?? 0;
    if (n < bestCount) {
      bestCount = n;
      best = c;
    }
  }
  return best;
}

// Black or white text for legibility on a hex background (WCAG relative luminance).
export function tagTextColor(hex: string): '#000000' | '#FFFFFF' {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // 0.179 is the crossover where contrast against black and white is equal.
  return L > 0.179 ? '#000000' : '#FFFFFF';
}
